/**
 * Playground SDK message model.
 *
 * Everything on the page-level bus is a BusMessage — Node-RED's
 * `{ topic, payload }` shape plus a device namespace and timestamp.
 * Topics follow `<device>/<signal>`; `$`-prefixed signals are reserved
 * for SDK system messages:
 *
 *   <device>/$state       "connecting" | "connected" | "disconnected" | "mismatch" | "error"
 *   <device>/$descriptor  parsed DESCRIBE payload (or null for legacy firmware)
 *   <device>/$raw         every inbound line/frame, unparsed
 *   <device>/$send        publish here to write a raw line to the device
 */

export type BusPayload =
  | string
  | number
  | boolean
  | null
  | BusPayload[]
  | { [key: string]: BusPayload };

export interface BusMessage {
  topic: string;
  payload: BusPayload;
  /** Device namespace (first topic segment) when the message maps to a device. */
  device?: string;
  /** Epoch milliseconds. */
  ts: number;
  /** Publisher tag — adapters skip messages they published themselves. */
  src?: string;
}

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "mismatch"
  | "error";

/** Signal or command entry inside a DESCRIBE descriptor. */
export interface DescriptorEntry {
  key: string;
  label?: string;
  unit?: string;
  min?: number;
  max?: number;
  type?: "number" | "string" | "boolean" | "json";
}

/**
 * Device self-description, returned by firmware in response to `DESCRIBE?`
 * as a single line: `DESCRIBE:{"device":"co2",...}`.
 * Borrowed from Firmata capability query / Homie / WoT Thing Description.
 */
export interface DeviceDescriptor {
  device: string;
  name?: string;
  version?: string;
  signals?: DescriptorEntry[];
  commands?: DescriptorEntry[];
}

export function parseDescriptor(value: unknown): DeviceDescriptor | null {
  if (typeof value !== "object" || value === null) return null;
  const obj = value as Record<string, unknown>;
  if (typeof obj.device !== "string" || obj.device.length === 0) return null;
  const entries = (raw: unknown): DescriptorEntry[] | undefined => {
    if (!Array.isArray(raw)) return undefined;
    const out: DescriptorEntry[] = [];
    for (const item of raw) {
      if (typeof item === "object" && item !== null && typeof (item as Record<string, unknown>).key === "string") {
        out.push(item as unknown as DescriptorEntry);
      }
    }
    return out;
  };
  return {
    device: obj.device,
    name: typeof obj.name === "string" ? obj.name : undefined,
    version: typeof obj.version === "string" ? obj.version : undefined,
    signals: entries(obj.signals),
    commands: entries(obj.commands),
  };
}
